import axios from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { Customer, CustomerAddress } from 'types/Customer';
import {
    isOnlineBookingSessionItem,
    isOnlineBookingSessionItemOf,
    OnlineBookingStep,
    SessionBusinessArea
} from 'types/OnlineBooking';
import {
    convertWalkingAvailability,
    convertWalkingEligibility,
    convertWalkingStaffAvailability,
    resolveWalkingCustomerAddressId,
    WalkingAvailability,
    WalkingAvailabilityDto,
    WalkingEligibility,
    WalkingEligibilityDto,
    WalkingStaffAvailability,
    WalkingStaffAvailabilityDto
} from 'types/Walking';
import { clearAuthToken } from 'actions/auth/AuthActions';
import { convertToCustomer, convertToCustomerAddressDto } from 'actions/customer/CustomerConverter';
import { CustomerDto } from 'actions/customer/CustomerDtos';
import { CustomerError, CustomerErrorType } from 'actions/customer/CustomerErrors';
import { ApiClient, ApiPublicClient, createTokenConfig } from 'api/ApiClient';
import { serializeToFormData } from 'api/serializer';
import {
    upsertMainCustomerAddress,
    walkingAddressFieldErrorMessage
} from 'actions/onlineBooking/actions/OnlineBookingWalkingAddress';
import {
    navigateToStep,
    setOnlineBookingCustomer,
    setOnlineBookingSelectedCategory
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    ONLINE_BOOKING_CLEAR_WALKING_ELIGIBILITY,
    ONLINE_BOOKING_GET_WALKING_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_WALKING_AVAILABILITY_START,
    ONLINE_BOOKING_GET_WALKING_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_FAILED,
    ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_START,
    ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_SUCCESS,
    ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_FAILED,
    ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_START,
    ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS,
    ONLINE_BOOKING_RECOVER_WALKING_BOOKING_ERROR
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import {
    buildWalkingAvailabilityRequestKey,
    buildWalkingEligibilityRequestKey,
    buildWalkingStaffAvailabilityRequestKey,
    serializeWalkingRequestParams,
    walkingAvailabilityCovers,
    walkingAvailabilityKeysForItems,
    walkingStaffAvailabilityKeysForItems
} from 'actions/onlineBooking/actions/OnlineBookingWalkingRequests';
import { getOnlineBookingWalkingAddressId } from 'reducers/onlineBooking/OnlineBookingSelectors';

const walkingEligibilityUrl = 'booking_eligibility';
const walkingStaffAvailabilityUrl = 'booking_staff_availability';
const walkingAvailabilityUrl = 'booking_availability';
const customersUrl = 'customers';

const walkingTokenConfig = () => ({
    ...createTokenConfig(),
    paramsSerializer: serializeWalkingRequestParams
});

const walkingEligibilityInFlight = new Map<string, Promise<WalkingEligibility>>();
const walkingStaffAvailabilityInFlight = new Map<string, Promise<WalkingStaffAvailability>>();
const walkingAvailabilityInFlight = new Map<string, Promise<WalkingAvailability>>();

const inFlightKey = (businessArea: SessionBusinessArea, requestKey: string) =>
    `${businessArea}|${requestKey}`;

const isUnauthorized = (error: unknown) =>
    axios.isAxiosError(error) && error.response?.status === 401;

const redirectToSigninOnUnauthorized = (error: unknown, dispatch: (action: unknown) => unknown) => {
    if (!isUnauthorized(error)) {
        return;
    }

    dispatch(clearAuthToken());
    dispatch(navigateToStep(OnlineBookingStep.Signin));
};

export const clearOnlineBookingWalkingEligibility = () => ({
    type: ONLINE_BOOKING_CLEAR_WALKING_ELIGIBILITY
});

export const fetchWalkingEligibilityThunk = (
    businessArea: SessionBusinessArea,
    addressId: number | undefined,
    numberOfPets: number
): ThunkAction<Promise<WalkingEligibility>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const marketplaceId = getState().marketplace.data.id;
        const requestKey = buildWalkingEligibilityRequestKey(
            marketplaceId,
            addressId,
            numberOfPets
        );
        const cached = getState().onlineBooking[businessArea].eligibility[requestKey];
        const pendingKey = inFlightKey(businessArea, requestKey);

        if (cached?.data && !cached.error) {
            return cached.data;
        }

        const inFlight = walkingEligibilityInFlight.get(pendingKey);
        if (inFlight) {
            return inFlight;
        }

        const pending = (async () => {
            dispatch({
                type: ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_START,
                payload: { businessArea, requestKey }
            });

            try {
                const { data } = await ApiPublicClient.get<WalkingEligibilityDto>(
                    walkingEligibilityUrl,
                    {
                        ...walkingTokenConfig(),
                        params: {
                            marketplace_id: marketplaceId,
                            business_area: businessArea,
                            address_id: addressId,
                            number_of_pets: numberOfPets
                        }
                    }
                );

                const eligibility = convertWalkingEligibility(data);

                dispatch({
                    type: ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_SUCCESS,
                    payload: { businessArea, requestKey, data: eligibility }
                });

                return eligibility;
            } catch (error) {
                redirectToSigninOnUnauthorized(error, dispatch);

                dispatch({
                    type: ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_FAILED,
                    payload: {
                        businessArea,
                        requestKey,
                        error: error instanceof Error ? error.message : String(error)
                    }
                });

                throw error;
            } finally {
                walkingEligibilityInFlight.delete(pendingKey);
            }
        })();

        walkingEligibilityInFlight.set(pendingKey, pending);
        return pending;
    };
};

export const fetchWalkingStaffAvailabilityThunk = (
    businessArea: SessionBusinessArea,
    serviceId: number,
    staffIds: Array<number>,
    addressId: number | undefined,
    numberOfPets: number,
    startDate: string,
    weeks: number
): ThunkAction<Promise<WalkingStaffAvailability>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const marketplaceId = getState().marketplace.data.id;
        const requestKey = buildWalkingStaffAvailabilityRequestKey(
            marketplaceId,
            addressId,
            numberOfPets,
            serviceId,
            staffIds,
            startDate,
            weeks
        );
        const cached = getState().onlineBooking[businessArea].staffAvailability[requestKey];
        const pendingKey = inFlightKey(businessArea, requestKey);

        if (cached?.data && !cached.error) {
            return cached.data;
        }

        const inFlight = walkingStaffAvailabilityInFlight.get(pendingKey);
        if (inFlight) {
            return inFlight;
        }

        const pending = (async () => {
            dispatch({
                type: ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_START,
                payload: { businessArea, requestKey }
            });

            try {
                if (staffIds.length === 0) {
                    const staffAvailability: WalkingStaffAvailability = { staff: [], reason: null };

                    dispatch({
                        type: ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS,
                        payload: { businessArea, requestKey, data: staffAvailability }
                    });

                    return staffAvailability;
                }

                const { data } = await ApiPublicClient.get<WalkingStaffAvailabilityDto>(
                    walkingStaffAvailabilityUrl,
                    {
                        ...walkingTokenConfig(),
                        params: {
                            marketplace_id: marketplaceId,
                            business_area: businessArea,
                            service_id: serviceId,
                            staff_ids: staffIds,
                            address_id: addressId,
                            number_of_pets: numberOfPets,
                            start_date: startDate,
                            weeks
                        }
                    }
                );

                const staffAvailability = convertWalkingStaffAvailability(data);

                dispatch({
                    type: ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS,
                    payload: { businessArea, requestKey, data: staffAvailability }
                });

                return staffAvailability;
            } catch (error) {
                redirectToSigninOnUnauthorized(error, dispatch);

                dispatch({
                    type: ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_FAILED,
                    payload: {
                        businessArea,
                        requestKey,
                        error: error instanceof Error ? error.message : String(error)
                    }
                });

                throw error;
            } finally {
                walkingStaffAvailabilityInFlight.delete(pendingKey);
            }
        })();

        walkingStaffAvailabilityInFlight.set(pendingKey, pending);
        return pending;
    };
};

export const fetchWalkingAvailabilityThunk = (
    businessArea: SessionBusinessArea,
    serviceId: number,
    addressId: number | undefined,
    staffId: number | undefined,
    startDate: string,
    numberOfPets: number,
    maxTimeframeWeeks: number,
    options: { force?: boolean } = {}
): ThunkAction<Promise<WalkingAvailability>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const marketplaceId = getState().marketplace.data.id;
        const requestKey = buildWalkingAvailabilityRequestKey(
            marketplaceId,
            addressId,
            numberOfPets,
            serviceId,
            staffId,
            maxTimeframeWeeks
        );
        const cached = getState().onlineBooking[businessArea].availability[requestKey];
        const pendingKey = inFlightKey(businessArea, requestKey);

        // `force` skips the cache (seats change between visits) but still joins an in-flight request.
        if (
            !options.force &&
            cached?.data &&
            !cached.error &&
            walkingAvailabilityCovers(cached.data, startDate)
        ) {
            return cached.data;
        }

        const inFlight = walkingAvailabilityInFlight.get(pendingKey);
        if (inFlight) {
            return inFlight;
        }

        const pending = (async () => {
            dispatch({
                type: ONLINE_BOOKING_GET_WALKING_AVAILABILITY_START,
                payload: { businessArea, requestKey }
            });

            try {
                const { data } = await ApiPublicClient.get<WalkingAvailabilityDto>(
                    walkingAvailabilityUrl,
                    {
                        ...walkingTokenConfig(),
                        params: {
                            marketplace_id: marketplaceId,
                            business_area: businessArea,
                            service_id: serviceId,
                            address_id: addressId,
                            staff_id: staffId,
                            start_date: startDate,
                            number_of_pets: numberOfPets,
                            max_timeframe_weeks: maxTimeframeWeeks
                        }
                    }
                );

                const availability = convertWalkingAvailability(data);

                dispatch({
                    type: ONLINE_BOOKING_GET_WALKING_AVAILABILITY_SUCCESS,
                    payload: { businessArea, requestKey, data: availability }
                });

                return availability;
            } catch (error) {
                redirectToSigninOnUnauthorized(error, dispatch);

                dispatch({
                    type: ONLINE_BOOKING_GET_WALKING_AVAILABILITY_FAILED,
                    payload: {
                        businessArea,
                        requestKey,
                        error: error instanceof Error ? error.message : String(error)
                    }
                });

                throw error;
            } finally {
                walkingAvailabilityInFlight.delete(pendingKey);
            }
        })();

        walkingAvailabilityInFlight.set(pendingKey, pending);
        return pending;
    };
};

export const saveWalkingCustomerAddressThunk = (
    address: CustomerAddress,
    businessArea: SessionBusinessArea
): ThunkAction<Promise<Customer>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        const customer = getState().onlineBooking.customer;
        const marketplaceId = getState().marketplace.data.id;
        const upserted = upsertMainCustomerAddress(customer.addresses, address);

        try {
            const formData = serializeToFormData({
                customer: {
                    addresses_attributes: [convertToCustomerAddressDto(upserted, marketplaceId)]
                }
            });

            const response = await ApiClient.patch<CustomerDto>(
                `${customersUrl}/${customer.id}`,
                formData,
                {
                    params: {
                        marketplace_id: marketplaceId
                    }
                }
            );

            const saved = convertToCustomer(response.data);

            dispatch(setOnlineBookingCustomer(saved));
            dispatch(clearOnlineBookingWalkingEligibility());

            const bookingItems = getState().onlineBooking.bookingItems;
            const addressId = resolveWalkingCustomerAddressId(saved.addresses);
            const areasToRefetch = new Set<SessionBusinessArea>([
                businessArea,
                ...bookingItems.filter(isOnlineBookingSessionItem).map(item => item.businessArea)
            ]);

            areasToRefetch.forEach(area => {
                const numberOfPets = bookingItems.filter(isOnlineBookingSessionItemOf(area)).length;

                void dispatch(fetchWalkingEligibilityThunk(area, addressId, numberOfPets)).catch(
                    () => undefined
                );
            });

            return saved;
        } catch (error) {
            const data = axios.isAxiosError(error) ? error.response?.data : undefined;

            throw new CustomerError(
                CustomerErrorType.Unknown,
                walkingAddressFieldErrorMessage(data)
            );
        }
    };
};

export const recoverWalkingCodedBookingErrorThunk = (
    businessArea: SessionBusinessArea,
    code?: string,
    itemUuid?: string
): ThunkAction<void, RootState, null, RootAction> => {
    return (dispatch, getState) => {
        const state = getState();
        const cache = state.onlineBooking[businessArea];
        const walkingItems = state.onlineBooking.bookingItems.filter(
            isOnlineBookingSessionItemOf(businessArea)
        );
        const addressId = getOnlineBookingWalkingAddressId(state);
        const marketplaceId = state.marketplace.data.id;
        const staffId = walkingItems[0]?.preferredStaff?.id;
        const availabilityKeysToRemove = walkingAvailabilityKeysForItems(
            Object.keys(cache.availability),
            walkingItems,
            marketplaceId,
            addressId,
            staffId
        );
        const staffAvailabilityKeysToRemove = walkingStaffAvailabilityKeysForItems(
            Object.keys(cache.staffAvailability),
            walkingItems,
            marketplaceId,
            addressId
        );

        dispatch({
            type: ONLINE_BOOKING_RECOVER_WALKING_BOOKING_ERROR,
            payload: {
                businessArea,
                code,
                itemUuid,
                availabilityKeysToRemove,
                staffAvailabilityKeysToRemove
            }
        });
        dispatch(setOnlineBookingSelectedCategory(businessArea));
        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };
};
